import { LogoMark } from "@shared/components/Logo";
import { cn } from "@ui/lib";
import {
	CheckCheckIcon,
	CheckIcon,
	MapPinIcon,
	PhoneOffIcon,
	ShoppingCartIcon,
} from "lucide-react";
import { DEMO_STORE, HERO_PRODUCTS } from "../site";
import { WhatsAppGlyph } from "./MkButton";

/**
 * Small, honest drawings of the product on each channel, made of the same parts as the real
 * screens. They carry no numbers we could not stand behind.
 */

const P = HERO_PRODUCTS;

/** A retailer's own app with the assistant open over it as a sheet. */
export function AppVisual({ className }: { className?: string }) {
	return (
		<div
			className={cn(
				"relative mx-auto h-[340px] w-full max-w-[300px] overflow-hidden rounded-t-[36px] bg-(--mk-white) p-4 pb-0",
				className,
			)}
		>
			<div className="flex items-center gap-2">
				<span className="size-7 rounded-lg bg-(--mk-mist-2)" />
				<span className="h-2 w-24 rounded-full bg-(--mk-mist-2)" />
				<ShoppingCartIcon
					className="ms-auto size-4 text-(--mk-muted)"
					aria-hidden
				/>
			</div>
			<div className="mt-4 grid grid-cols-3 gap-2 opacity-50">
				{[P.milk, P.doritos, P.cola, P.hummus, P.cashews, P.eggs].map(
					(p) => (
						<div
							key={p.id}
							className="rounded-xl bg-(--mk-mist) p-1.5"
						>
							<img
								src={p.thumb}
								alt=""
								width={64}
								height={64}
								loading="lazy"
								className="aspect-square w-full object-contain"
							/>
						</div>
					),
				)}
			</div>
			<div className="absolute inset-x-2 bottom-0 rounded-t-[28px] bg-(--mk-white) p-4 shadow-[0_-24px_48px_-24px_oklch(0.2_0.004_270/0.35)]">
				<div className="mx-auto mb-3 h-1 w-10 rounded-full bg-(--mk-mist-2)" />
				<div className="flex items-center gap-2">
					<LogoMark className="size-6" />
					<span className="font-medium text-sm">Ask the store</span>
				</div>
				<p className="ms-auto mt-3 w-fit rounded-2xl rounded-br-md bg-(--mk-ink) px-3 py-1.5 text-(--mk-white) text-xs">
					Lunch boxes for 3 kids this week
				</p>
				<p className="mt-2 flex items-center gap-2 rounded-xl bg-(--mk-signal-soft) px-3 py-2 text-xs">
					<CheckIcon
						className="size-3.5 text-(--mk-signal)"
						aria-hidden
					/>
					Added 9 items to your cart
				</p>
			</div>
		</div>
	);
}

/** A WhatsApp conversation that ends in a placed order. */
export function WhatsAppVisual({ className }: { className?: string }) {
	return (
		<div
			className={cn(
				"mx-auto flex w-full max-w-[320px] flex-col gap-2 rounded-t-[36px] bg-(--mk-white) p-4 text-[13px]",
				className,
			)}
		>
			<div className="flex items-center gap-2.5 border-(--mk-line) border-b pb-3">
				<span className="grid size-8 place-items-center rounded-full bg-(--mk-mist)">
					<WhatsAppGlyph className="size-4" />
				</span>
				<span>
					<span className="block font-medium text-sm">
						{DEMO_STORE.name}
					</span>
					<span className="block text-(--mk-muted) text-[11px]">
						Business account
					</span>
				</span>
			</div>
			<p className="ms-auto max-w-[80%] rounded-2xl rounded-tr-md bg-(--mk-mist-2) px-3 py-2">
				Same as last week, plus 2 hummus
			</p>
			<div className="max-w-[88%] rounded-2xl rounded-tl-md bg-(--mk-mist) px-3 py-2">
				Your basket: 11 items, $38.20. Delivered today from your nearest
				branch.
				<span className="mt-2 flex items-center justify-center rounded-xl bg-(--mk-white) py-1.5 font-medium text-xs">
					View basket
				</span>
			</div>
			<div className="max-w-[88%] rounded-2xl rounded-tl-md bg-(--mk-mist) px-3 py-2">
				Ready to check out?
				<span className="mt-2 flex items-center justify-center rounded-xl bg-(--mk-ink) py-1.5 font-medium text-(--mk-white) text-xs">
					Checkout · $38.20
				</span>
			</div>
			<p className="flex items-center gap-1 self-end pb-3 text-(--mk-muted) text-[11px]">
				<CheckCheckIcon
					className="size-3.5 text-(--mk-signal)"
					aria-hidden
				/>
				Order placed · arriving 6:30 pm
			</p>
		</div>
	);
}

/** A live call with the store's assistant, the basket filling as it talks. */
export function CallVisual({ className }: { className?: string }) {
	return (
		<div
			className={cn(
				"mx-auto flex w-full max-w-[300px] flex-col items-center rounded-t-[36px] bg-(--mk-night) px-5 pt-7 pb-5 text-(--mk-on-night)",
				className,
			)}
		>
			<LogoMark className="size-14" />
			<p className="mt-3 font-semibold text-[15px]">{DEMO_STORE.name}</p>
			<p className="text-(--mk-on-night-muted) text-xs tabular-nums">
				Voice assistant · 0:42
			</p>
			<span
				aria-hidden
				className="mk-bars mt-4 flex h-6 items-center gap-[3px]"
			>
				{BARS.map((bar, i) => (
					<span
						key={bar.id}
						className="block w-[3px] rounded-full bg-(--mk-signal-night)"
						style={{
							height: `${bar.height * 100}%`,
							animationDelay: `${i * -0.11}s`,
						}}
					/>
				))}
			</span>
			<p className="mt-4 text-center text-sm leading-snug">
				“Abu Kass rice and a kilo of chicken are in your basket.
				Anything else?”
			</p>
			<span className="mt-5 grid size-11 place-items-center rounded-full bg-(--mk-stop)">
				<PhoneOffIcon className="size-[18px]" aria-hidden />
			</span>
		</div>
	);
}

const BARS = [0.4, 0.8, 0.55, 1, 0.65, 0.9, 0.5, 0.75, 0.45].map(
	(height, i) => ({ id: `bar-${i}`, height }),
);

/** The retailer's inbox: a shopper asked for a person, and someone can take over. */
export function InboxVisual({ className }: { className?: string }) {
	const rows = [
		{
			id: "rana",
			initials: "RM",
			hue: 25,
			name: "Rana M.",
			via: "WhatsApp",
			text: "Can I change the delivery address on my last order?",
			flag: "Asked for a person",
		},
		{
			id: "omar",
			initials: "OK",
			hue: 250,
			name: "Omar K.",
			via: "Call",
			text: "Basket confirmed on the keypad, 7 items",
		},
		{
			id: "lea",
			initials: "LH",
			hue: 150,
			name: "Lea H.",
			via: "App",
			text: "Gluten-free bread for a party of 10",
		},
	];
	return (
		<div
			className={cn(
				"mx-auto w-full max-w-[460px] rounded-[24px] bg-(--mk-white) p-2",
				className,
			)}
		>
			{rows.map((row, i) => (
				<div
					key={row.id}
					className={cn(
						"flex items-center gap-3 rounded-2xl p-3",
						i === 0 && "bg-(--mk-mist)",
					)}
				>
					<span
						className="grid size-9 shrink-0 place-items-center rounded-full font-semibold text-xs"
						style={{
							background: `oklch(0.92 0.05 ${row.hue})`,
							color: `oklch(0.38 0.1 ${row.hue})`,
						}}
					>
						{row.initials}
					</span>
					<div className="min-w-0 flex-1">
						<p className="flex items-center gap-2 text-sm">
							<span className="font-medium">{row.name}</span>
							<span className="text-(--mk-faint) text-xs">
								{row.via}
							</span>
						</p>
						<p className="truncate text-(--mk-muted) text-[13px]">
							{row.text}
						</p>
					</div>
					{row.flag ? (
						<span className="shrink-0 rounded-full bg-(--mk-ink) px-3 py-1.5 font-medium text-(--mk-white) text-xs">
							Take over
						</span>
					) : null}
				</div>
			))}
		</div>
	);
}

/** Where the shopper is decides the prices: a pin, then the branch that serves it. */
export function BranchVisual({ className }: { className?: string }) {
	return (
		<div
			className={cn(
				"mx-auto w-full max-w-[360px] rounded-[24px] bg-(--mk-white) p-4 text-[13px]",
				className,
			)}
		>
			<p className="flex items-center gap-2 font-medium">
				<MapPinIcon className="size-4 text-(--mk-signal)" aria-hidden />
				Delivering from your nearest branch
			</p>
			<div className="mt-3 space-y-1.5">
				{[
					{ p: P.milk, price: "1.70" },
					{ p: P.eggs, price: "2.99" },
					{ p: P.chicken, price: "10.25" },
				].map(({ p, price }) => (
					<div
						key={p.id}
						className="flex items-center gap-3 rounded-xl bg-(--mk-mist) p-2"
					>
						<img
							src={p.thumb}
							alt=""
							width={36}
							height={36}
							loading="lazy"
							className="size-9 object-contain"
						/>
						<span className="min-w-0 flex-1 truncate">
							{p.title}
						</span>
						<span className="font-semibold tabular-nums">
							${price}
						</span>
					</div>
				))}
			</div>
		</div>
	);
}
